import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  EditOutlined,
  PlusSquareOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { Button, Card, Drawer, Form, Space, Table, Tag, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { TablePaginationConfig } from 'antd/es/table'
import moment from 'moment'

import { useNavigate } from 'react-router-dom'
import { useAppContext } from '@/components/AppContext'
import withDefaultLayout from '@/hoc/WithDefaultLayout'
import { Shrine } from '@a4b/api/src/modules/Temple/types'
import EventCalendarForm from './components/EventCalendarForm'
import {
  formatEventCalendarCreatePayload,
  formatEventCalendarUpdatePayload,
  parseEventCalendarToFormFields,
} from './utils/helper'

type EventCalendarRow = Shrine.EventCalendar
type TithiRow = Shrine.Tithi

const ManageEventCalendar: React.FC = () => {
  const { networkInstance } = useAppContext()
  const navigate = useNavigate()
  const [form] = Form.useForm()

  const [loadingCalendars, setLoadingCalendars] = useState(false)
  const [loadingTithis, setLoadingTithis] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [eventCalendars, setEventCalendars] = useState<EventCalendarRow[]>([])
  const [tithis, setTithis] = useState<TithiRow[]>([])

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingCalendar, setEditingCalendar] =
    useState<EventCalendarRow | null>(null)

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  })

  const tithiById = useMemo(() => {
    const map = new Map<string, TithiRow>()
    for (const tithi of tithis) {
      if (tithi?.id) map.set(tithi.id, tithi)
    }
    return map
  }, [tithis])

  const tithiOptions = useMemo(() => {
    return tithis
      .filter(x => !!x?.id)
      .map(x => {
        const label = x.name || x.display_name_en || x.id
        return {
          value: x.id,
          label: `${label} (${x.id})`,
        }
      })
  }, [tithis])

  const fetchEventCalendars = useCallback(async () => {
    setLoadingCalendars(true)
    try {
      const response =
        await networkInstance.clientWithHeaders.shrineApi.getEventCalendars()
      const list = response?.data?.data ?? []
      setEventCalendars(list)
      setPagination(prev => ({ ...prev, total: list.length }))
    } catch (err) {
      message.error('Failed to fetch event calendars')
      console.error(err)
    } finally {
      setLoadingCalendars(false)
    }
  }, [networkInstance])

  const fetchTithis = useCallback(async () => {
    setLoadingTithis(true)
    try {
      const response =
        await networkInstance.clientWithHeaders.shrineApi.getTithis()
      const list = response?.data?.data ?? []
      setTithis(list)
    } catch (err) {
      message.error('Failed to fetch tithis')
      console.error(err)
    } finally {
      setLoadingTithis(false)
    }
  }, [networkInstance])

  useEffect(() => {
    fetchEventCalendars()
    fetchTithis()
  }, [fetchEventCalendars, fetchTithis])

  const openCreate = useCallback(() => {
    setEditingCalendar(null)
    form.resetFields()
    form.setFieldsValue({ active: true, position: 0 })
    setDrawerOpen(true)
  }, [form])

  const openEdit = useCallback(
    (record: EventCalendarRow) => {
      setEditingCalendar(record)
      form.setFieldsValue(parseEventCalendarToFormFields(record))
      setDrawerOpen(true)
    },
    [form],
  )

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false)
    setEditingCalendar(null)
    form.resetFields()
  }, [form])

  const handleSubmit = useCallback(
    async (values: any) => {
      if (submitting) return
      setSubmitting(true)
      try {
        if (editingCalendar?.id) {
          const payload = formatEventCalendarUpdatePayload(
            values,
            editingCalendar.id,
          )
          await networkInstance.clientWithHeaders.shrineApi.updateEventCalendar(
            editingCalendar.id,
            payload,
          )
          message.success('Event calendar updated')
        } else {
          const payload = formatEventCalendarCreatePayload(values)
          await networkInstance.clientWithHeaders.shrineApi.createEventCalendar(
            payload,
          )
          message.success('Event calendar created')
        }
        closeDrawer()
        fetchEventCalendars()
      } catch (err) {
        message.error('Failed to save event calendar')
        console.error(err)
      } finally {
        setSubmitting(false)
      }
    },
    [
      editingCalendar,
      networkInstance,
      closeDrawer,
      fetchEventCalendars,
      submitting,
    ],
  )

  const columns: ColumnsType<EventCalendarRow> = [
    {
      title: 'S.No',
      key: 'index',
      render: (_, __, index) =>
        (pagination.current! - 1) * pagination.pageSize! + index + 1,
    },
    {
      title: 'Date',
      dataIndex: 'event_date',
      key: 'event_date',
      render: text => (text ? moment(text).format('DD MMM YYYY') : '-'),
    },
    {
      title: 'Event ID',
      dataIndex: 'id',
      key: 'id',
      // width:200,
      render: (entityId: any) => {
        const key = entityId != null ? String(entityId) : ''
        const tithi = tithiById.get(key)
        if (!tithi) return key
        const name = tithi.display_name_en || tithi.name || tithi.id
        return `${name} (${tithi.id})`
      },
    },
    {
      title: 'Tithi ID',
      dataIndex: 'entity_id',
      key: 'entity_id',
      // width:200,
      render: (entityId: any) => {
        const key = entityId != null ? String(entityId) : ''
        const tithi = tithiById.get(key)
        if (!tithi) return key
        const name = tithi.display_name_en || tithi.name || tithi.id
        return `${name} (${tithi.id})`
      },
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: val =>
        val ? <Tag color='green'>TRUE</Tag> : <Tag color='red'>FALSE</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size='small'>
          <Button
            type='link'
            icon={<SettingOutlined />}
            onClick={() =>
              navigate(`/shraddha-shorts/event-calendar/${record.id}/cards`)
            }
          >
            Cards
          </Button>
          <Button
            type='link'
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ]

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination(newPagination)
  }

  return (
    <Card
      className='card-flex-column-grow'
      title='Shraddha Shorts - Event Calendar'
      extra={
        <Button
          type='primary'
          icon={<PlusSquareOutlined />}
          onClick={openCreate}
        >
          Add Event Calendar
        </Button>
      }
    >
      <div className='full-size-flex-column'>
        <Table<EventCalendarRow>
          rowKey='id'
          loading={loadingCalendars}
          columns={columns}
          dataSource={eventCalendars}
          pagination={pagination}
          onChange={handleTableChange}
        />

        <Drawer
          title={
            editingCalendar ? 'Edit Event Calendar' : 'Create Event Calendar'
          }
          width={520}
          visible={drawerOpen}
          onClose={closeDrawer}
          destroyOnClose
        >
          <EventCalendarForm
            form={form}
            onFinish={handleSubmit}
            onCancel={closeDrawer}
            isEditing={!!editingCalendar}
            tithiOptions={tithiOptions}
            loadingTithis={loadingTithis}
            submitting={submitting}
          />
        </Drawer>
      </div>
    </Card>
  )
}

export default withDefaultLayout(ManageEventCalendar)
